import React, { createContext, useState, useContext, useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import themes from '../themes';
import axios from 'axios';
import '../themes/themes.css';
import { applyCssVariables } from '../themes/themeLoader';
import { loadThemeFont } from '../themes/fontLoader';

// Create the context
const ThemeContext = createContext({
  currentTheme: 'situation-room-default',
  changeTheme: () => {},
  themeFromUrl: null
});

// Hook to use the theme context
export const useTheme = () => useContext(ThemeContext);

// Helper function to get theme from URL parameters
const getThemeFromUrl = () => {
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    const themeParam = urlParams.get('theme');
    // Check if the theme from URL exists in our available themes
    if (themeParam && themes[themeParam]) {
      return themeParam;
    }
  }
  return null;
};

// Function to add theme parameter to a URL if it exists in current URL
export const addThemeToUrl = (url) => {
  if (typeof window !== 'undefined') {
    const urlTheme = getThemeFromUrl();
    if (urlTheme) {
      // Check if the URL already has parameters
      const hasParams = url.includes('?');
      // Add theme parameter
      return `${url}${hasParams ? '&' : '?'}theme=${urlTheme}`;
    }
  }
  return url;
};

// Provider component
export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState('situation-room-default');
  const [themeFromUrl, setThemeFromUrl] = useState(null);

  // Check for theme in URL parameters and database on component mount
  useEffect(() => {
    const fetchThemeSetting = async () => {
      try {
        // First check if theme is specified in URL
        const urlTheme = getThemeFromUrl();
        if (urlTheme) {
          setCurrentTheme(urlTheme);
          setThemeFromUrl(urlTheme);
          return;
        }

        // If no URL theme, fetch from database
        const response = await axios.get('/api/settings/theme');
        if (response.data && response.data.value) {
          const themeName = response.data.value;
          // Check if the theme exists in our available themes
          if (themes[themeName]) {
            setCurrentTheme(themeName);
          }
        }
      } catch (error) {
        console.error('Failed to fetch theme setting:', error);
      }
    };

    fetchThemeSetting();

    // Listen for URL changes to update theme if needed
    const handleLocationChange = () => {
      const urlTheme = getThemeFromUrl();
      if (urlTheme) {
        setCurrentTheme(urlTheme);
        setThemeFromUrl(urlTheme);
      } else if (themeFromUrl) {
        // If there was a URL theme but now it's gone, get theme from database
        fetchThemeSetting();
        setThemeFromUrl(null);
      }
    };

    window.addEventListener('popstate', handleLocationChange);

    // Patch all anchor elements to maintain theme parameter
    const patchAnchors = () => {
      const urlTheme = getThemeFromUrl();
      if (!urlTheme) return;

      const observer = new MutationObserver((mutations) => {
        mutations.forEach(mutation => {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach(node => {
              if (node.nodeType === 1) { // Element node
                const anchors = node.querySelectorAll ? node.querySelectorAll('a') : [];
                anchors.forEach(anchor => patchAnchor(anchor, urlTheme));
                
                // Also check if the node itself is an anchor
                if (node.tagName === 'A') {
                  patchAnchor(node, urlTheme);
                }
              }
            });
          }
        });
      });

      // Function to patch a single anchor element
      const patchAnchor = (anchor, themeParam) => {
        // Skip external links, anchors, or links with theme already
        if (anchor.href && 
            anchor.hostname === window.location.hostname && 
            !anchor.href.includes('#') &&
            !anchor.href.includes('theme=')) {
          
          const url = new URL(anchor.href);
          url.searchParams.set('theme', themeParam);
          anchor.href = url.toString();
        }
      };

      // Patch existing anchors first
      document.querySelectorAll('a').forEach(anchor => patchAnchor(anchor, urlTheme));

      // Start observing the document
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      return () => observer.disconnect();
    };

    const cleanup = patchAnchors();
    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      if (cleanup) cleanup();
    };
  }, [themeFromUrl]);

  // Apply data-theme attribute and load theme resources when theme changes
  useEffect(() => {
    if (currentTheme) {
      // Update HTML data attribute
      document.documentElement.setAttribute('data-theme', currentTheme);
      
      // Apply CSS variables
      if (typeof applyCssVariables === 'function') {
        applyCssVariables(currentTheme);
      }
      
      // Load the appropriate font
      loadThemeFont(currentTheme);
    }
  }, [currentTheme]);

  // Store dynamic theme if an object is passed
  const [dynamicTheme, setDynamicTheme] = useState(null);

  // Function to change the theme
  const changeTheme = (themeNameOrObject) => {
    if (typeof themeNameOrObject === 'object') {
      setDynamicTheme(themeNameOrObject);
      setCurrentTheme('dynamic');
    } else if (themes[themeNameOrObject]) {
      setDynamicTheme(null);
      setCurrentTheme(themeNameOrObject);
    }
  };

  // Get the selected theme object
  const theme = currentTheme === 'dynamic' && dynamicTheme ? dynamicTheme : (themes[currentTheme] || themes['situation-room-default']);

  return (
    <ThemeContext.Provider value={{ currentTheme, changeTheme, themeFromUrl }}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

// Export everything: ThemeProvider, context, and utility functions
export { ThemeContext, getThemeFromUrl };
export default ThemeProvider; 